import { useState } from 'react';
import { X, Plus, Check } from 'lucide-react';
import { clsx } from 'clsx';
import type { Product } from '../../types';

interface ComparisonProduct {
  product: Product;
  specs: Array<{ key: string; label: string; value: string; is_highlight?: boolean }>;
}

interface ProductComparisonProps {
  products: ComparisonProduct[];
  onRemove: (productId: string) => void;
  onClear: () => void;
}

export function ProductComparison({ products, onRemove, onClear }: ProductComparisonProps) {
  const [activeTab, setActiveTab] = useState<'specs' | 'prices' | 'all'>('specs');

  if (products.length === 0) {
    return null;
  }

  // Get all unique spec keys across all products
  const allSpecKeys = Array.from(
    new Set(
      products.flatMap(p => p.specs.map(s => s.key))
    )
  );

  // Highlight specs (is_highlight = true)
  const highlightSpecs = Array.from(
    new Set(
      products.flatMap(p => p.specs.filter(s => s.is_highlight).map(s => s.key))
    )
  );

  // Create spec comparison rows
  const specRows = allSpecKeys.map(key => {
    const firstLabel = products[0].specs.find(s => s.key === key)?.label || key;
    return {
      key,
      label: firstLabel,
      values: products.map(p => {
        const spec = p.specs.find(s => s.key === key);
        return spec?.value || '-';
      }),
      isHighlight: highlightSpecs.includes(key),
    };
  });

  return (
    <div className="bg-white rounded-xl shadow-lg border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-blue-600 to-indigo-600 px-6 py-4 flex items-center justify-between">
        <div className="flex items-center gap-3">
          <h2 className="text-xl font-bold text-white">제품 비교</h2>
          <span className="bg-white/20 text-white px-3 py-1 rounded-full text-sm">
            {products.length}개 선택
          </span>
        </div>
        <button
          onClick={onClear}
          className="text-white/80 hover:text-white transition-colors text-sm flex items-center gap-1"
        >
          초기화
        </button>
      </div>

      {/* Tabs */}
      <div className="border-b border-gray-200 px-6">
        <div className="flex gap-6">
          <button
            onClick={() => setActiveTab('specs')}
            className={clsx(
              'py-3 px-1 border-b-2 transition-colors text-sm font-medium',
              activeTab === 'specs' || activeTab === 'all'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            )}
          >
            스펵 비교
          </button>
          <button
            onClick={() => setActiveTab('prices')}
            className={clsx(
              'py-3 px-1 border-b-2 transition-colors text-sm font-medium',
              activeTab === 'prices'
                ? 'border-blue-600 text-blue-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            )}
          >
            가격 비교
          </button>
        </div>
      </div>

      {/* Products */}
      {(activeTab === 'specs' || activeTab === 'all') && (
        <div className="overflow-x-auto">
          {/* Product Headers */}
          <div className="grid gap-4 p-6 border-b border-gray-200 min-w-[max-content]"
               style={{ gridTemplateColumns: `150px repeat(${products.length}, 1fr)` }}>
            <div></div>
            {products.map(({ product }) => (
              <div key={product.id} className="relative">
                <button
                  onClick={() => onRemove(product.id)}
                  className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 text-white rounded-full flex items-center justify-center hover:bg-red-600 transition-colors opacity-0 group-hover:opacity-100"
                >
                  <X size={14} />
                </button>
                {product.images && product.images[0] && (
                  <img
                    src={product.images[0].url}
                    alt={product.images[0].alt || product.name}
                    className="w-24 h-24 object-contain rounded-lg mx-auto mb-2"
                  />
                )}
                <p className="text-sm text-gray-500 mb-1">{product.brand.name}</p>
                <p className="font-semibold text-gray-900 text-sm line-clamp-2">{product.name}</p>
                <p className="text-xs text-gray-400 mt-1">{product.category.name}</p>
              </div>
            ))}
          </div>

          {/* Highlight Specs */}
          {highlightSpecs.length > 0 && (
            <div className="bg-blue-50/50">
              <div className="grid gap-4 p-4" style={{ gridTemplateColumns: `150px repeat(${products.length}, 1fr)` }}>
                <div className="text-sm font-medium text-blue-700">핵심 스펙</div>
                {products.map(({ product }) => (
                  <div key={product.id} className="space-y-1">
                    {product.specs
                      .filter(s => s.is_highlight)
                      .map(spec => (
                        <div key={spec.key} className="bg-white rounded-lg p-2 text-center">
                          <p className="text-xs text-gray-500 mb-1">{spec.label}</p>
                          <p className="text-sm font-bold text-gray-900">{spec.value}</p>
                        </div>
                      ))}
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* All Specs Comparison */}
          <div className="divide-y divide-gray-100">
            {specRows.map(row => (
              <div
                key={row.key}
                className={clsx(
                  'grid gap-4 px-6 py-3 hover:bg-gray-50',
                  row.isHighlight ? 'bg-blue-50/50 font-medium' : ''
                )}
                style={{ gridTemplateColumns: `150px repeat(${products.length}, 1fr)` }}
              >
                <div className="text-sm text-gray-600">{row.label}</div>
                {row.values.map((value, idx) => {
                  const allSame = row.values.every(v => v === value);
                  return (
                    <div
                      key={idx}
                      className={clsx(
                        'text-sm py-1 px-2 rounded',
                        allSame ? 'text-gray-500' : 'font-semibold text-gray-900',
                        allSame ? '' : 'bg-yellow-50'
                      )}
                    >
                      {value}
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Price Comparison */}
      {activeTab === 'prices' && (
        <div className="overflow-x-auto">
          <div className="grid gap-4 p-6" style={{ gridTemplateColumns: `150px repeat(${products.length}, 1fr)` }}>
            <div></div>
            {products.map(({ product }) => (
              <div key={product.id} className="text-center">
                {product.prices && product.prices.length > 0 ? (
                  <>
                    {product.prices
                      .slice()
                      .sort((a, b) => a.price - b.price)
                      .map((price, idx) => (
                        <div key={idx} className="mb-2">
                          {idx === 0 && (
                            <div className="inline-flex items-center gap-1 bg-green-100 text-green-700 px-2 py-1 rounded text-xs mb-2">
                              <Check size={12} />
                              최저가
                            </div>
                          )}
                          <p className="text-lg font-bold text-blue-600">
                            {price.price.toLocaleString()}원
                          </p>
                          <p className="text-xs text-gray-500">{price.store.name}</p>
                        </div>
                      ))}
                  </>
                ) : (
                  <p className="text-gray-400">가격 정보 없음</p>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Footer */}
      <div className="bg-gray-50 px-6 py-3 text-xs text-gray-500 text-center">
        쿠팡 파트너스 활동의 일환으로 수수료를 제공받을 수 있습니다.
      </div>
    </div>
  );
}

// Comparison Sidebar (floating)
interface ComparisonSidebarProps {
  products: ComparisonProduct[];
  onRemove: (productId: string) => void;
  onClear: () => void;
  onCompare?: () => void;
}

export function ComparisonSidebar({ products, onRemove, onClear, onCompare }: ComparisonSidebarProps) {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <>
      {/* Toggle Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={clsx(
          'fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-3 rounded-full shadow-lg transition-all duration-300',
          isOpen ? 'bg-blue-600 text-white' : 'bg-gray-800 text-white hover:bg-gray-700'
        )}
      >
        <Plus size={20} />
        <span className="font-medium">비교함</span>
        {products.length > 0 && (
          <span className="bg-red-500 text-white w-6 h-6 rounded-full flex items-center justify-center text-xs">
            {products.length}
          </span>
        )}
      </button>

      {/* Sidebar */}
      {isOpen && (
        <div className="fixed top-0 right-0 w-full max-w-md h-full bg-white shadow-2xl z-50 flex flex-col">
          <div className="p-6 border-b border-gray-200 flex items-center justify-between bg-gray-50">
            <div>
              <h2 className="text-xl font-bold text-gray-900">제품 비교</h2>
              <p className="text-sm text-gray-600 mt-1">{products.length}개 제품 선택</p>
            </div>
            <button
              onClick={() => setIsOpen(false)}
              className="p-2 hover:bg-gray-200 rounded-lg transition-colors"
            >
              <X size={20} className="text-gray-600" />
            </button>
          </div>

          <div className="flex-1 overflow-y-auto p-6 space-y-4">
            {products.map(({ product }) => (
              <div key={product.id} className="bg-gray-50 rounded-lg p-4 flex gap-4">
                {product.images && product.images[0] && (
                  <img
                    src={product.images[0].url}
                    alt={product.images[0].alt || product.name}
                    className="w-20 h-20 object-contain rounded-lg flex-shrink-0"
                  />
                )}
                <div className="flex-1 min-w-0">
                  <p className="text-sm text-gray-500">{product.brand.name}</p>
                  <p className="font-semibold text-gray-900 line-clamp-1">{product.name}</p>
                  {product.prices && product.prices[0] && (
                    <p className="text-sm font-bold text-blue-600 mt-1">
                      {product.prices[0].price.toLocaleString()}원
                    </p>
                  )}
                </div>
                <button
                  onClick={() => onRemove(product.id)}
                  className="p-2 hover:bg-gray-200 rounded-lg transition-colors flex-shrink-0"
                >
                  <X size={16} className="text-gray-500" />
                </button>
              </div>
            ))}

            {products.length === 0 && (
              <div className="text-center py-12 text-gray-400">
                <p>비교할 제품을 선택해주세요</p>
                <p className="text-sm mt-2">제품 카드에서 "비교" 버튼을 클릭하세요</p>
              </div>
            )}
          </div>

          {products.length >= 2 && (
            <div className="p-6 border-t border-gray-200">
              <button
                onClick={() => {
                  setIsOpen(false);
                  onCompare?.();
                }}
                className="w-full py-3 bg-blue-600 text-white rounded-lg font-medium hover:bg-blue-700 transition-colors"
              >
                상세 비교하기
              </button>
            </div>
          )}

          {products.length > 0 && (
            <div className="p-6 border-t border-gray-200">
              <button
                onClick={onClear}
                className="w-full py-2 text-gray-600 hover:text-gray-900 text-sm"
              >
                모두 제거
              </button>
            </div>
          )}
        </div>
      )}
    </>
  );
}

// Empty state for when no products are selected
export function ComparisonEmptyState() {
  return (
    <div className="text-center py-12 px-6 bg-gray-50 rounded-xl">
      <Plus size={48} className="mx-auto text-gray-300 mb-4" />
      <h3 className="text-lg font-semibold text-gray-900 mb-2">제품 비교</h3>
      <p className="text-gray-600 text-sm">
        제품을 2개 이상 선택하여 상세 정보를 비교해보세요
      </p>
    </div>
  );
}
